import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { EventCard } from '@/components/display/EventCard';
import { NoEventsCard } from '@/components/display/NoEventsCard';
import { useIsDesktop } from '@/lib/hooks/useMediaQuery';
import type { EventCardListProps } from '@/types/events';

/**
 * Generates a heading string from a pattern and month name.
 *
 * @param pattern - Heading pattern containing `{month}` placeholder
 * @param month - Month name to substitute into the pattern
 * @returns The resolved heading string
 */
function resolveHeading(pattern: string, month: string): string {
  return pattern.replace('{month}', month);
}

/**
 * EventCardList - Section component that displays event cards grouped by month.
 *
 * Shows the current month's events first with an "Events in [Month]" heading,
 * followed by future months with "Upcoming events for [Month]" headings.
 * Events within each month are displayed in a responsive grid (3 columns on
 * desktop, 1 column on tablet/mobile). When a month has no events, a
 * NoEventsCard is displayed instead.
 *
 * @example
 * ```tsx
 * import { EventCardList } from '@/components/sections/EventCardList';
 *
 * <EventCardList
 *   content={{
 *     currentMonthHeadingPattern: "Events in {month}",
 *     futureMonthHeadingPattern: "Upcoming events for {month}",
 *     noEventsCard: { title: "No events at this time", message: "Check back soon." },
 *   }}
 *   months={monthsData}
 *   categoryThemeColors={themeColors}
 *   savedEventIds={new Set()}
 *   onShareEvent={(event) => console.log('Share', event)}
 *   onToggleSaveEvent={(id) => console.log('Toggle save', id)}
 * />
 * ```
 */
export const EventCardList = forwardRef<HTMLDivElement, EventCardListProps>(
  (
    {
      content,
      months,
      categoryThemeColors,
      savedEventIds,
      onShareEvent,
      onToggleSaveEvent,
      className,
    },
    ref
  ) => {
    // Hooks must be called before any conditional returns
    const isDesktop = useIsDesktop();

    if (!content || !months || months.length === 0) return null;

    return (
      <div
        ref={ref}
        className={cn('flex flex-col gap-8', className)}
        data-testid="event-card-list"
      >
        {months.map((monthData) => {
          const headingPattern = monthData.isCurrent
            ? content.currentMonthHeadingPattern
            : content.futureMonthHeadingPattern;
          const heading = resolveHeading(headingPattern, monthData.month);
          const hasEvents = monthData.events.length > 0;

          return (
            <section
              key={`${monthData.month}-${monthData.year}`}
              aria-labelledby={`month-heading-${monthData.month}-${monthData.year}`}
            >
              <h2
                id={`month-heading-${monthData.month}-${monthData.year}`}
                className={cn(
                  typography.headingLarge,
                  'text-foreground mb-4'
                )}
              >
                {heading}
              </h2>

              {hasEvents ? (
                <div
                  className={cn(
                    'grid gap-4',
                    isDesktop
                      ? 'grid-cols-3'
                      : 'grid-cols-1'
                  )}
                >
                  {monthData.events.map((event) => {
                    const themeColors =
                      categoryThemeColors[event.category] ?? {
                        background: '#E5E5E566',
                        border: '#E5E5E5',
                      };

                    return (
                      <EventCard
                        key={event.id}
                        content={event}
                        themeColors={themeColors}
                        isSaved={savedEventIds.has(event.id)}
                        onShare={onShareEvent}
                        onToggleSave={onToggleSaveEvent}
                      />
                    );
                  })}
                </div>
              ) : (
                <NoEventsCard content={content.noEventsCard} />
              )}
            </section>
          );
        })}
      </div>
    );
  }
);

EventCardList.displayName = 'EventCardList';
